import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Phone, Save, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

// 50 states + DC. Code first so the cascade keys stay aligned with backend.
const US_STATES = [
  ['AL','Alabama'],   ['AK','Alaska'],    ['AZ','Arizona'],
  ['AR','Arkansas'],  ['CA','California'],['CO','Colorado'],
  ['CT','Connecticut'],['DE','Delaware'], ['DC','District of Columbia'],
  ['FL','Florida'],   ['GA','Georgia'],   ['HI','Hawaii'],
  ['ID','Idaho'],     ['IL','Illinois'],  ['IN','Indiana'],
  ['IA','Iowa'],      ['KS','Kansas'],    ['KY','Kentucky'],
  ['LA','Louisiana'], ['ME','Maine'],     ['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'], ['MT','Montana'],
  ['NE','Nebraska'],  ['NV','Nevada'],    ['NH','New Hampshire'],
  ['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],  ['OR','Oregon'],    ['PA','Pennsylvania'],
  ['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'], ['TX','Texas'],     ['UT','Utah'],
  ['VT','Vermont'],   ['VA','Virginia'],  ['WA','Washington'],
  ['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

/**
 * Format a string of digits / mixed input into an E.164 number (US default).
 * Returns null if not enough digits to be a valid number.
 */
const toE164 = (raw) => {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits.length >= 7 ? `+${digits}` : null;
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits.length >= 7 ? `+${digits}` : null;
};

const formatDisplay = (e164) => {
  if (!e164) return '';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164;
};

const StatePhoneNumbersPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [registered, setRegistered] = useState({}); // code -> { phone_number, ... }
  const [drafts, setDrafts] = useState({}); // code -> input string
  const [savingState, setSavingState] = useState(null);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const res = await api.get('/twilio/admin/phone-numbers');
      const map = {};
      for (const it of res.data?.phone_numbers || []) {
        map[(it.state || '').toUpperCase()] = it;
      }
      setRegistered(map);
    } catch (e) {
      toast.error('Could not load state phone numbers');
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (code) => {
    const draft = (drafts[code] || '').trim();
    const e164 = toE164(draft);
    if (!e164) {
      toast.error('Enter a valid phone number (e.g. +1 704 741 9110)');
      return;
    }
    setSavingState(code);
    try {
      await api.post(`/twilio/admin/phone-numbers?state=${encodeURIComponent(code)}&phone_number=${encodeURIComponent(e164)}`);
      toast.success(`${code}: number saved & assigned to all users in state`);
      setDrafts((p) => ({ ...p, [code]: '' }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSavingState(null);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Remove the ${code} state number? Users currently assigned this number will have their ORB Phone cleared.`)) return;
    setSavingState(code);
    try {
      await api.delete(`/twilio/admin/phone-numbers/${encodeURIComponent(code)}`);
      toast.success(`${code} number removed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove');
    } finally {
      setSavingState(null);
    }
  };

  const filteredStates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return US_STATES;
    return US_STATES.filter(([code, name]) =>
      code.toLowerCase().includes(q) || name.toLowerCase().includes(q)
    );
  }, [search]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <p className="text-muted-foreground text-sm mt-1">You don't have access to this page.</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="state-phone-numbers-page">
      <div className="max-w-3xl mx-auto p-3">
        <div className="flex items-center gap-2 mb-4">
          <Button size="icon" variant="ghost" onClick={() => navigate('/admin/calling')} data-testid="phone-numbers-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="w-6 h-6 text-[#586D52]" />
            State Phone Numbers
          </h1>
        </div>

        <Card className="rounded-2xl mb-3">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            Add one Twilio phone number per state. The number you save here becomes
            the <strong>ORB Phone</strong> for every user (current and future) onboarded
            into that state. To override a single user, edit them in
            User Administration → Edit Contact and pick from the dropdown.
          </CardContent>
        </Card>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search states"
            className="pl-9"
            data-testid="state-search-input"
          />
        </div>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">All States</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {filteredStates.map(([code, name]) => {
              const reg = registered[code];
              const draft = drafts[code] ?? '';
              const isSaving = savingState === code;
              return (
                <div key={code} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-[#1a1a1a]/60" data-testid={`state-row-${code}`}>
                  <div className="sm:w-44 flex-shrink-0">
                    <p className="font-medium text-sm">{name}</p>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{code}</p>
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      value={draft || (reg?.phone_number ? formatDisplay(reg.phone_number) : '')}
                      onChange={(e) => setDrafts((p) => ({ ...p, [code]: e.target.value }))}
                      placeholder={reg ? '' : '+1 (555) 123-4567'}
                      className="h-9 text-sm"
                      data-testid={`state-input-${code}`}
                    />
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => save(code)}
                      disabled={isSaving || (!draft && !!reg && draft === '')}
                      className="bg-[#586D52] hover:bg-[#4a5d46] h-9"
                      data-testid={`state-save-${code}`}
                    >
                      <Save className="w-3.5 h-3.5 mr-1" />
                      Save
                    </Button>
                    {reg && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 h-9"
                        onClick={() => remove(code)}
                        disabled={isSaving}
                        data-testid={`state-delete-${code}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StatePhoneNumbersPage;
